import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'

interface SearchResult {
  type: string
  label: string
  meta: string
  icon: string
  path: string
}

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const NAV_COMMANDS: SearchResult[] = [
  { type: 'nav', label: 'Dashboard', meta: '', icon: '⌂', path: '/' },
  { type: 'nav', label: 'Clients', meta: '', icon: '◉', path: '/clients' },
  { type: 'nav', label: 'Sites', meta: '', icon: '⊞', path: '/sites' },
  { type: 'nav', label: 'Proposals', meta: '', icon: '☰', path: '/proposals' },
  { type: 'nav', label: 'Dispatch', meta: '', icon: '✉', path: '/dispatch' },
  { type: 'nav', label: 'Tickets', meta: '', icon: '🎫', path: '/tickets' },
  { type: 'nav', label: 'Knowledge Base', meta: '', icon: '📖', path: '/kb' },
  { type: 'nav', label: 'Products', meta: '', icon: '◫', path: '/products' },
  { type: 'nav', label: 'Pipeline', meta: '', icon: '◈', path: '/pipeline' },
  { type: 'nav', label: 'Invoices', meta: '', icon: '◈', path: '/invoices' },
  { type: 'nav', label: 'Settings', meta: '', icon: '⚙', path: '/settings' },
]

const CREATE_COMMANDS: SearchResult[] = [
  { type: 'create', label: 'New Client', meta: '', icon: '+', path: '/clients?action=new' },
  { type: 'create', label: 'New Proposal', meta: '', icon: '+', path: '/proposals?action=new' },
  { type: 'create', label: 'New Work Order', meta: '', icon: '+', path: '/dispatch?action=new' },
  { type: 'create', label: 'New Site', meta: '', icon: '+', path: '/sites?action=new' },
  { type: 'create', label: 'New Ticket', meta: '', icon: '+', path: '/tickets?action=new' },
]

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  // Listen for Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        onOpenChange(!open)
      }
      if (e.key === 'Escape' && open) {
        onOpenChange(false)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onOpenChange])

  // Focus input on open
  useEffect(() => {
    if (open) {
      setQuery('')
      setResults([...NAV_COMMANDS, ...CREATE_COMMANDS])
      setActiveIndex(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Debounced search
  useEffect(() => {
    if (!open) return

    if (!query.trim()) {
      setResults([...NAV_COMMANDS, ...CREATE_COMMANDS])
      setActiveIndex(0)
      return
    }

    const q = query.toLowerCase()
    const navMatches = NAV_COMMANDS.filter(c => c.label.toLowerCase().includes(q))
    const createMatches = CREATE_COMMANDS.filter(c => c.label.toLowerCase().includes(q))
    const localMatches = [...navMatches, ...createMatches]

    // Show local matches immediately
    setResults(localMatches)
    setActiveIndex(0)

    // Then search backend
    const timer = setTimeout(async () => {
      try {
        const res = await api.get<{ results: SearchResult[] }>(`/search?q=${encodeURIComponent(query)}`)
        setResults([...localMatches, ...res.results])
        setActiveIndex(0)
      } catch {
        // Fall back to just local matches
      }
    }, 200)

    return () => clearTimeout(timer)
  }, [query, open])

  const navigateTo = useCallback((result: SearchResult) => {
    onOpenChange(false)
    navigate(result.path)
  }, [navigate, onOpenChange])

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(prev => Math.min(prev + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(prev => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter' && results[activeIndex]) {
      navigateTo(results[activeIndex])
    }
  }

  if (!open) return null

  // Group results by type
  const groups = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    const key = r.type
    if (!acc[key]) acc[key] = []
    acc[key].push(r)
    return acc
  }, {})

  const groupLabels: Record<string, string> = {
    nav: 'Navigate',
    create: 'Create',
    client: 'Clients',
    site: 'Sites',
    device: 'Devices',
    proposal: 'Proposals',
    product: 'Products',
  }

  let globalIdx = 0

  return (
    <div
      onClick={() => onOpenChange(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,.6)',
        backdropFilter: 'blur(16px) saturate(1.5)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '15vh',
        animation: 'in-fade 150ms',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="glass-heavy"
        style={{
          width: 560, borderRadius: 'var(--r-xl)', overflow: 'hidden',
          animation: 'in-scale 200ms var(--ease-sp)',
        }}
      >
        {/* Input */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,.06)',
        }}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            placeholder="Search clients, sites, devices, proposals..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              color: 'var(--text-1)', fontSize: 15, fontFamily: 'var(--font)',
            }}
          />
          <kbd style={{
            fontSize: 10, fontFamily: 'var(--mono)', padding: '2px 6px',
            borderRadius: 4, background: 'var(--bg-3)', color: 'var(--text-3)',
          }}>ESC</kbd>
        </div>

        {/* Results */}
        <div style={{ maxHeight: 340, overflowY: 'auto', padding: 6 }}>
          {results.length === 0 && query && (
            <p style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-3)' }}>
              No results for &apos;{query}&apos;
            </p>
          )}
          {Object.entries(groups).map(([type, items]) => (
            <div key={type}>
              <div style={{
                fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em',
                color: 'var(--text-3)', padding: '10px 12px 4px',
              }}>
                {groupLabels[type] || type}
              </div>
              {items.map((r) => {
                const idx = globalIdx++
                const isActive = idx === activeIndex
                return (
                  <div
                    key={`${r.type}-${r.path}-${idx}`}
                    onClick={() => navigateTo(r)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', borderRadius: 'var(--r-m)', cursor: 'pointer',
                      background: isActive ? 'rgba(255,255,255,.05)' : 'transparent',
                      color: isActive ? 'var(--text-1)' : 'var(--text-2)',
                      fontSize: 13, transition: 'background var(--dur-f)',
                    }}
                  >
                    <span style={{
                      width: 28, height: 28, borderRadius: 'var(--r-s)',
                      background: 'rgba(255,255,255,.04)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, flexShrink: 0,
                    }}>
                      {r.icon}
                    </span>
                    <span style={{ flex: 1 }}>{r.label}</span>
                    {r.meta && (
                      <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>
                        {r.meta}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', gap: 16, padding: '8px 14px',
          borderTop: '1px solid rgba(255,255,255,.06)',
          fontSize: 11, color: 'var(--text-3)',
        }}>
          <span><kbd style={{
            fontSize: 10, fontFamily: 'var(--mono)', background: 'var(--bg-3)',
            padding: '1px 4px', borderRadius: 3, marginRight: 3,
          }}>↑↓</kbd> navigate</span>
          <span><kbd style={{
            fontSize: 10, fontFamily: 'var(--mono)', background: 'var(--bg-3)',
            padding: '1px 4px', borderRadius: 3, marginRight: 3,
          }}>⏎</kbd> open</span>
          <span><kbd style={{
            fontSize: 10, fontFamily: 'var(--mono)', background: 'var(--bg-3)',
            padding: '1px 4px', borderRadius: 3, marginRight: 3,
          }}>esc</kbd> close</span>
        </div>
      </div>
    </div>
  )
}
